import {i18n} from '@online-offer/i18n';
import {type MoneyResponseBody} from '@online-offer/shared';
import {Box, Display, HStack, Show, Text, VStack} from 'platform/foundation';
import {formatNumber, useFormatCurrency, useLocale} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {EMPTY_PLACEHOLDER, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {type DisplayedFinancialProductQuote} from 'features/financial-products';

import {
  FinancialProductPreferenceCoordinates,
  FinancialProductType,
} from '../../api/types/onlineOfferTypes';
import {FinancialProductResultRow} from './FinancialProductResultRow';
import {formatFinancialProductMoney} from './formatFinancialProductMoney';
import {formatFinancialProductPercentage} from './formatFinancialProductPercentage';
import {getLeasingTargetGroupLabelKey} from './getLeasingTargetGroupLabelKey';

interface FinancialProductOfferDetailProps extends TestIdProps {
  productType: FinancialProductType;
  coordinates: FinancialProductPreferenceCoordinates;
  displayed: DisplayedFinancialProductQuote;
  /** No quote can be shown and the save of the shown choice keeps failing. */
  isQuoteFailed: boolean;
  /** Leasing only: the offer's monthly services, null when it has none. */
  monthlyServicesTotal?: MoneyResponseBody | Nullish;
}

/**
 * Amounts come from the displayed quote (the stored backend quote, or the local one computed from
 * the published rates) without recalculation; rate brackets stay hidden. Each product type always
 * renders the same rows, so the panel keeps its height whether a quote is stored, local or absent.
 * A leasing with monthly services headlines the total the customer pays each month, explained by
 * the vehicle payment and the services beneath it; without them it headlines the vehicle payment.
 */
export function FinancialProductOfferDetail(props: FinancialProductOfferDetailProps) {
  const formatCurrency = useFormatCurrency();
  const locale = useLocale();
  const coordinates = props.coordinates;
  const calculation = match(props.displayed)
    .with({source: 'NONE'}, () => null)
    .otherwise((displayed) => displayed.quote.calculation);

  const formatMoney = (money: MoneyResponseBody | Nullish) =>
    formatFinancialProductMoney(money, formatCurrency);

  const formatPercentageWithAmount = (
    percentage: number | Nullish,
    amount: MoneyResponseBody | Nullish
  ) =>
    isNil(percentage)
      ? formatMoney(amount)
      : [formatFinancialProductPercentage(percentage), formatMoney(amount)]
          .filter(isNotNil)
          .join(' · ');

  // The one-line slot above the amount carries the failure, so a failed save never adds a line.
  const metadata = match({
    ...coordinates,
    productType: props.productType,
    isQuoteFailed: props.isQuoteFailed,
  })
    .with({isQuoteFailed: true}, () => i18n.t('financialProducts.unavailable.generic'))
    .with({termMonths: Pattern.nullish}, () => EMPTY_PLACEHOLDER)
    .with({productType: 'LEASING'}, (leasing) =>
      i18n.t('financialProducts.resultLeasing', {term: leasing.termMonths})
    )
    .with({downPaymentPercentage: Pattern.nullish}, (financing) =>
      i18n.t('financialProducts.monthsValue', {value: financing.termMonths})
    )
    .otherwise((financing) =>
      i18n.t('financialProducts.result', {
        term: financing.termMonths,
        downPayment: financing.downPaymentPercentage,
      })
    );

  const balloonFigures =
    props.productType === 'BALLOON'
      ? [
          {
            label: i18n.t('financialProducts.balloonPayment'),
            value: formatPercentageWithAmount(
              coordinates.balloonPaymentPercentage,
              calculation?.balloonPaymentAmount
            ),
          },
        ]
      : [];

  const figures = match(props.productType)
    .with('LEASING', () => [
      {
        label: i18n.t('financialProducts.customerType'),
        value: isNil(coordinates.leasingTargetGroup)
          ? null
          : i18n.t(getLeasingTargetGroupLabelKey(coordinates.leasingTargetGroup)),
      },
      {
        label: i18n.t('financialProducts.annualMileage'),
        value: isNil(coordinates.annualMileage)
          ? null
          : i18n.t('financialProducts.kilometresPerYearValue', {
              value: formatNumber(locale.localeConfig, coordinates.annualMileage, 0),
            }),
      },
    ])
    .otherwise(() => [
      {
        label: i18n.t('financialProducts.downPayment'),
        value: formatPercentageWithAmount(
          coordinates.downPaymentPercentage,
          calculation?.downPaymentAmount
        ),
      },
      ...balloonFigures,
      {
        label: i18n.t('financialProducts.nominalRate'),
        value: isNil(calculation?.nominalRate)
          ? null
          : formatFinancialProductPercentage(calculation.nominalRate),
      },
      {
        label: i18n.t('financialProducts.apr'),
        value: isNil(calculation?.apr) ? null : formatFinancialProductPercentage(calculation.apr),
      },
      {
        label: i18n.t('financialProducts.netCredit'),
        value: formatMoney(calculation?.netCredit),
      },
      {
        label: i18n.t('financialProducts.grossCredit'),
        value: formatMoney(calculation?.grossCredit),
      },
      {
        label: i18n.t('financialProducts.totalInterest'),
        value: formatMoney(calculation?.totalInterest),
      },
      {
        label: i18n.t('financialProducts.totalPayable'),
        value: formatMoney(calculation?.totalPayable),
        emphasized: true,
      },
    ]);

  return (
    <VStack spacing={5} data-testid={suffixTestId('offer', props)}>
      <VStack spacing={1}>
        <Box opacity={0.62}>
          <span
            aria-live="polite"
            data-testid={suffixTestId(props.isQuoteFailed ? 'quoteFailed' : 'metadata', props)}
          >
            <Text size="small" color="inverted" noWrap>
              {metadata}
            </Text>
          </span>
        </Box>
        <HStack spacing={2} align="baseline" wrap>
          <div data-testid={`${suffixTestId('monthlyPayment', props)}-heading`}>
            <Display size={1} headingLevel={2} color="inverted">
              {formatMoney(
                calculation?.leasingTotalMonthlyPayment ?? calculation?.monthlyPayment
              ) ?? EMPTY_PLACEHOLDER}
            </Display>
          </div>
          <Box opacity={0.74}>
            <Text size="large" color="inverted">
              {i18n.t('financialProducts.perMonth')}
            </Text>
          </Box>
        </HStack>
        {/* The line explains the services total, so it shows only when that total is the headline. */}
        <Show
          when={
            isNotNil(calculation?.leasingTotalMonthlyPayment) ? props.monthlyServicesTotal : null
          }
        >
          {(monthlyServicesTotal) => (
            <Box opacity={0.74}>
              <Text
                size="small"
                color="inverted"
                data-testid={suffixTestId('leasingCostsBreakdown', props)}
              >
                {i18n.t('financialProducts.leasingCosts.breakdown', {
                  vehiclePayment: formatMoney(calculation?.monthlyPayment) ?? EMPTY_PLACEHOLDER,
                  services: formatMoney(monthlyServicesTotal),
                })}
              </Text>
            </Box>
          )}
        </Show>
      </VStack>
      <VStack spacing={0}>
        {figures.map((figure) => (
          <FinancialProductResultRow
            key={figure.label}
            {...figure}
            data-testid={props['data-testid']}
          />
        ))}
      </VStack>
    </VStack>
  );
}
